<script lang="ts" setup>
import { popupStack } from '@/utils/popups.ts';
import eventBus from '@/utils/event-bus';
import { type Ref, ref } from 'vue';
import Modal from './Modal.vue';

const promptValues: Ref<{ [id: string]: string }> = ref({});

function setPromptValue(id: string, event: Event) {
  promptValues.value[id] = (event.target as HTMLInputElement).value;
}

function emitPromptValue(id: string) {
  eventBus.emit(id, promptValues.value[id] || '');
  delete promptValues.value[id];
}
</script>

<template>
  <modal
    v-for="item in popupStack"
    :key="item.id"
    cancelable
    :z-index="2000"
    @close="eventBus.emit(item.id, false)"
  >
    <transition
      appear
      name="fade"
    >
      <div
        v-if="item.type === 'confirm'"
        class="card sm:rounded-b-box bg-base-300 w-[95vw] max-w-[95vw] rounded-b-none sm:max-w-2xl sm:border-2"
      >
        <div class="card-body">
          <div class="flex flex-col items-center gap-4 sm:flex-row">
            <icon
              v-if="item.iconName"
              :class="item.iconClasses"
              :icon="item.iconName"
              alt="Confirm popup icon"
            />
            <div
              v-if="item.title"
              class="card-title justify-center"
            >
              {{ item.title }}
            </div>
          </div>
          <div class="text-center">{{ item.body }}</div>

          <div class="card-actions mt-3 flex w-full flex-row justify-center gap-3">
            <button
              class="btn w-full sm:w-1/3"
              :class="item.okButtonClasses"
              @click="eventBus.emit(item.id, true)"
            >
              {{ item.okButtonText }}
            </button>
            <button
              class="btn btn-ghost w-full sm:w-1/3"
              @click="eventBus.emit(item.id, false)"
            >
              {{ item.cancelButtonText }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-else-if="item.type === 'prompt'"
        class="card card-bordered sm:rounded-b-box bg-base-100 w-[95vw] max-w-[95vw] rounded-b-none sm:max-w-2xl"
      >
        <div class="card-body">
          <div
            v-if="item.title"
            class="card-title justify-center"
          >
            {{ item.title }}
          </div>
          <div class="flex flex-col items-center gap-4 sm:flex-row">
            <icon
              v-if="item.iconName"
              :class="item.iconClasses"
              :icon="item.iconName"
              alt="Prompt popup icon"
            />
            <div>{{ item.body }}</div>
          </div>
          <input
            v-if="item.inputType !== 'textarea'"
            v-focus
            :placeholder="item.inputPlaceholder || ''"
            :type="item.inputType"
            class="input input-bordered w-full"
            @input="setPromptValue(item.id, $event)"
          />
          <textarea
            v-else
            v-focus
            :placeholder="item.inputPlaceholder || ''"
            class="textarea textarea-bordered w-full"
            rows="1"
            @input="setPromptValue(item.id, $event)"
          ></textarea>

          <div class="card-actions mt-3 flex w-full flex-row justify-center gap-3">
            <button
              v-if="item.cancelButtonText"
              class="btn btn-ghost w-full sm:w-1/3"
              @click="eventBus.emit(item.id, null)"
            >
              {{ item.cancelButtonText }}
            </button>
            <button
              :disabled="!promptValues[item.id]"
              class="btn w-full sm:w-1/3"
              :class="item.okButtonClasses"
              @click="emitPromptValue(item.id)"
            >
              {{ item.okButtonText }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-else-if="item.type === 'alert'"
        class="card card-bordered sm:rounded-b-box bg-base-100 w-[95vw] max-w-[95vw] rounded-b-none sm:max-w-2xl"
      >
        <div class="card-body">
          <div
            v-if="item.title"
            class="card-title"
          >
            {{ item.title }}
          </div>
          <div class="flex flex-col items-center gap-4 sm:flex-row">
            <icon
              v-if="item.iconName"
              :class="item.iconClasses"
              :icon="item.iconName"
              alt="Alert popup icon"
            />
            <div>{{ item.body }}</div>
          </div>

          <div class="card-actions mt-3 flex w-full flex-row justify-center">
            <button
              class="btn w-full sm:w-1/3"
              :class="item.okButtonClasses"
              @click="eventBus.emit(item.id, true)"
            >
              {{ item.okButtonText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </modal>
</template>

<style lang="scss" scoped>
.inset-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
